/**
 * Chess rules: FEN, legal move generation, SAN, check and checkmate, shared by the
 * `chess-mate` puzzle type and the `chess` duel (ADR 0076). Port of
 * `backend/apps/rules/chess.py`, kept in step by the perft corpus in
 * `shared/fixtures/chess_mate/perft.json`. Not a solver: nothing here searches for a mate.
 *
 * Squares are `0..63` with `a1 = 0`, `h1 = 7`, `a8 = 56`. Pieces are FEN letters, uppercase
 * white, lowercase black, `""` for an empty square.
 */

export const FILES = "abcdefgh";
export type Color = "w" | "b";
export type Piece = "P" | "N" | "B" | "R" | "Q" | "K" | "p" | "n" | "b" | "r" | "q" | "k" | "";
export type Promotion = "q" | "r" | "b" | "n";
export const PROMOTIONS: readonly Promotion[] = ["q", "r", "b", "n"];
const PIECES = "PNBRQK";
const CASTLING_ORDER = "KQkq";
export const UCI_RE = /^([a-h][1-8])([a-h][1-8])([qrbn])?$/;

type Delta = readonly [number, number];
const KNIGHT_DELTAS: readonly Delta[] = [
  [1, 2],
  [2, 1],
  [2, -1],
  [1, -2],
  [-1, -2],
  [-2, -1],
  [-2, 1],
  [-1, 2],
];
const KING_DELTAS: readonly Delta[] = [
  [0, 1],
  [1, 1],
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, -1],
  [-1, 0],
  [-1, 1],
];
const BISHOP_RAYS: readonly Delta[] = [
  [1, 1],
  [1, -1],
  [-1, -1],
  [-1, 1],
];
const ROOK_RAYS: readonly Delta[] = [
  [0, 1],
  [1, 0],
  [0, -1],
  [-1, 0],
];
const QUEEN_RAYS: readonly Delta[] = [...BISHOP_RAYS, ...ROOK_RAYS];

interface CastlingSpec {
  kingFrom: number;
  kingTo: number;
  rookFrom: number;
  rookTo: number;
  empties: readonly number[];
  crossings: readonly number[];
}
const CASTLING: Record<string, CastlingSpec> = {
  K: { kingFrom: 4, kingTo: 6, rookFrom: 7, rookTo: 5, empties: [5, 6], crossings: [4, 5, 6] },
  Q: { kingFrom: 4, kingTo: 2, rookFrom: 0, rookTo: 3, empties: [1, 2, 3], crossings: [4, 3, 2] },
  k: {
    kingFrom: 60,
    kingTo: 62,
    rookFrom: 63,
    rookTo: 61,
    empties: [61, 62],
    crossings: [60, 61, 62],
  },
  q: {
    kingFrom: 60,
    kingTo: 58,
    rookFrom: 56,
    rookTo: 59,
    empties: [57, 58, 59],
    crossings: [60, 59, 58],
  },
};

export interface Move {
  from: number;
  to: number;
  promotion?: Promotion;
}

export interface Position {
  board: Piece[];
  turn: Color;
  /** Subset of "KQkq", in that order. */
  castling: string;
  ep: number | null;
  halfmove: number;
  fullmove: number;
}

export class FenError extends Error {}

export function squareName(square: number): string {
  return FILES[square % 8] + String(Math.floor(square / 8) + 1);
}

export function parseSquare(name: string): number {
  if (name.length !== 2 || !FILES.includes(name[0]) || !"12345678".includes(name[1]))
    throw new FenError(`bad square ${name}`);
  return (Number(name[1]) - 1) * 8 + FILES.indexOf(name[0]);
}

export function fileOf(square: number): number {
  return square % 8;
}

export function rankOf(square: number): number {
  return Math.floor(square / 8);
}

export function colorOf(piece: Piece): Color {
  return piece === piece.toUpperCase() ? "w" : "b";
}

export function other(color: Color): Color {
  return color === "w" ? "b" : "w";
}

export function uci(move: Move): string {
  return squareName(move.from) + squareName(move.to) + (move.promotion ?? "");
}

export function sameMove(a: Move, b: Move): boolean {
  return a.from === b.from && a.to === b.to && (a.promotion ?? "") === (b.promotion ?? "");
}

// ---- FEN ---------------------------------------------------------------------------------

export function parseFen(fen: string): Position {
  const fields = fen.trim().split(/\s+/);
  if (fields.length < 4 || fields.length > 6) throw new FenError("expected 4 to 6 fields");
  const [placement, turn, castlingField, epField] = fields;
  const halfmove = fields[4] ?? "0";
  const fullmove = fields[5] ?? "1";

  const ranks = placement.split("/");
  if (ranks.length !== 8) throw new FenError("expected 8 ranks");
  const board: Piece[] = Array.from({ length: 64 }, () => "");
  ranks.forEach((rank, rankIndex) => {
    const rankNumber = 7 - rankIndex;
    let file = 0;
    for (const char of rank) {
      if ("12345678".includes(char)) {
        file += Number(char);
      } else if (PIECES.includes(char) || PIECES.toLowerCase().includes(char)) {
        if (file > 7) throw new FenError(`rank ${rankNumber + 1} is too long`);
        board[rankNumber * 8 + file] = char as Piece;
        file += 1;
      } else {
        throw new FenError(`unexpected character ${char}`);
      }
    }
    if (file !== 8) throw new FenError(`rank ${rankNumber + 1} does not have 8 squares`);
  });

  if (turn !== "w" && turn !== "b") throw new FenError("side to move must be w or b");

  let castling = "";
  if (castlingField !== "-") {
    const letters = [...castlingField];
    if (
      letters.some((c) => !CASTLING_ORDER.includes(c)) ||
      new Set(letters).size !== letters.length
    )
      throw new FenError("bad castling field");
    castling = [...CASTLING_ORDER].filter((c) => letters.includes(c)).join("");
  }

  let ep: number | null = null;
  if (epField !== "-") {
    ep = parseSquare(epField);
    const expectedRank = turn === "w" ? 5 : 2;
    const pawn = turn === "w" ? "p" : "P";
    const behind = turn === "w" ? ep - 8 : ep + 8;
    if (rankOf(ep) !== expectedRank || board[ep] !== "" || board[behind] !== pawn)
      throw new FenError("en passant square does not match the board");
  }

  if (!/^\d+$/.test(halfmove) || !/^\d+$/.test(fullmove) || Number(fullmove) < 1)
    throw new FenError("bad move counters");

  return { board, turn, castling, ep, halfmove: Number(halfmove), fullmove: Number(fullmove) };
}

export function toFen(position: Position): string {
  const ranks: string[] = [];
  for (let rank = 7; rank >= 0; rank -= 1) {
    let text = "";
    let empty = 0;
    for (let file = 0; file < 8; file += 1) {
      const piece = position.board[rank * 8 + file];
      if (piece === "") {
        empty += 1;
      } else {
        if (empty) text += String(empty);
        empty = 0;
        text += piece;
      }
    }
    if (empty) text += String(empty);
    ranks.push(text);
  }
  return [
    ranks.join("/"),
    position.turn,
    position.castling || "-",
    position.ep === null ? "-" : squareName(position.ep),
    String(position.halfmove),
    String(position.fullmove),
  ].join(" ");
}

/** Semantic problems a syntactically fine FEN may still have. Codes, localized by the UI. */
export function validate(position: Position): string[] {
  const codes: string[] = [];
  const { board } = position;
  const count = (piece: Piece) => board.filter((p) => p === piece).length;
  if (count("K") !== 1 || count("k") !== 1) codes.push("king_count");
  const backRanks = [
    ...Array.from({ length: 8 }, (_, i) => i),
    ...Array.from({ length: 8 }, (_, i) => 56 + i),
  ];
  if (backRanks.some((s) => board[s] === "P" || board[s] === "p")) codes.push("pawn_on_back_rank");
  for (const letter of position.castling) {
    const spec = CASTLING[letter];
    const king = letter === letter.toUpperCase() ? "K" : "k";
    const rook = letter === letter.toUpperCase() ? "R" : "r";
    if (board[spec.kingFrom] !== king || board[spec.rookFrom] !== rook) {
      codes.push("invalid_castling");
      break;
    }
  }
  if (!codes.includes("king_count")) {
    const opponent = other(position.turn);
    if (isAttacked(position, kingSquare(position, opponent), position.turn))
      codes.push("opponent_in_check");
  }
  return codes;
}

// ---- attacks -----------------------------------------------------------------------------

export function kingSquare(position: Position, color: Color): number {
  return position.board.indexOf(color === "w" ? "K" : "k");
}

function step(square: number, dFile: number, dRank: number): number | null {
  const file = fileOf(square) + dFile;
  const rank = rankOf(square) + dRank;
  return file >= 0 && file < 8 && rank >= 0 && rank < 8 ? rank * 8 + file : null;
}

/** Whether `by` (a colour) attacks `square`, regardless of whose turn it is. */
export function isAttacked(position: Position, square: number, by: Color): boolean {
  const { board } = position;
  const letters = by === "w" ? PIECES : PIECES.toLowerCase();
  const [pawn, knight, bishop, rook, queen, king] = letters;
  const pawnRankStep = by === "w" ? -1 : 1;
  for (const dFile of [-1, 1]) {
    const origin = step(square, dFile, pawnRankStep);
    if (origin !== null && board[origin] === pawn) return true;
  }
  for (const [dFile, dRank] of KNIGHT_DELTAS) {
    const origin = step(square, dFile, dRank);
    if (origin !== null && board[origin] === knight) return true;
  }
  for (const [dFile, dRank] of KING_DELTAS) {
    const origin = step(square, dFile, dRank);
    if (origin !== null && board[origin] === king) return true;
  }
  const scans: [readonly Delta[], string[]][] = [
    [BISHOP_RAYS, [bishop, queen]],
    [ROOK_RAYS, [rook, queen]],
  ];
  for (const [rays, sliders] of scans) {
    for (const [dFile, dRank] of rays) {
      let current = step(square, dFile, dRank);
      while (current !== null) {
        const piece = board[current];
        if (piece !== "") {
          if (sliders.includes(piece)) return true;
          break;
        }
        current = step(current, dFile, dRank);
      }
    }
  }
  return false;
}

export function isCheck(position: Position): boolean {
  return isAttacked(position, kingSquare(position, position.turn), other(position.turn));
}

// ---- move generation ---------------------------------------------------------------------

function pseudoMoves(position: Position): Move[] {
  const { board, turn: color } = position;
  const moves: Move[] = [];
  const forward = color === "w" ? 8 : -8;
  const startRank = color === "w" ? 1 : 6;
  const lastRank = color === "w" ? 7 : 0;

  const push = (from: number, to: number, pawn = false) => {
    if (pawn && rankOf(to) === lastRank) {
      for (const promotion of PROMOTIONS) moves.push({ from, to, promotion });
    } else {
      moves.push({ from, to });
    }
  };

  for (let square = 0; square < 64; square += 1) {
    const piece = board[square];
    if (piece === "" || colorOf(piece) !== color) continue;
    const kind = piece.toUpperCase();
    if (kind === "P") {
      const ahead = square + forward;
      if (ahead >= 0 && ahead < 64 && board[ahead] === "") {
        push(square, ahead, true);
        const double = ahead + forward;
        if (rankOf(square) === startRank && board[double] === "")
          moves.push({ from: square, to: double });
      }
      for (const dFile of [-1, 1]) {
        const target = step(square, dFile, color === "w" ? 1 : -1);
        if (target === null) continue;
        const victim = board[target];
        if ((victim !== "" && colorOf(victim) !== color) || target === position.ep)
          push(square, target, true);
      }
    } else if (kind === "N" || kind === "K") {
      for (const [dFile, dRank] of kind === "N" ? KNIGHT_DELTAS : KING_DELTAS) {
        const target = step(square, dFile, dRank);
        if (target === null) continue;
        const victim = board[target];
        if (victim === "" || colorOf(victim) !== color) push(square, target);
      }
    } else {
      const rays = kind === "B" ? BISHOP_RAYS : kind === "R" ? ROOK_RAYS : QUEEN_RAYS;
      for (const [dFile, dRank] of rays) {
        let target = step(square, dFile, dRank);
        while (target !== null) {
          const victim = board[target];
          if (victim !== "") {
            if (colorOf(victim) !== color) push(square, target);
            break;
          }
          push(square, target);
          target = step(target, dFile, dRank);
        }
      }
    }
  }

  for (const letter of position.castling) {
    if ((letter === letter.toUpperCase()) !== (color === "w")) continue;
    const spec = CASTLING[letter];
    const king = color === "w" ? "K" : "k";
    const rook = color === "w" ? "R" : "r";
    if (board[spec.kingFrom] !== king || board[spec.rookFrom] !== rook) continue;
    if (spec.empties.some((s) => board[s] !== "")) continue;
    if (spec.crossings.some((s) => isAttacked(position, s, other(color)))) continue;
    moves.push({ from: spec.kingFrom, to: spec.kingTo });
  }
  return moves;
}

/** Applies a pseudo-legal move; the caller checks legality with `legalMoves`. */
export function makeMove(position: Position, move: Move): Position {
  const board = [...position.board];
  const color = position.turn;
  const piece = board[move.from];
  let captured = board[move.to];
  const kind = piece.toUpperCase();
  const forward = color === "w" ? 8 : -8;

  if (kind === "P" && move.to === position.ep && captured === "") {
    board[move.to - forward] = "";
    captured = "p";
  }
  board[move.from] = "";
  if (move.promotion) {
    board[move.to] = (color === "w" ? move.promotion.toUpperCase() : move.promotion) as Piece;
  } else {
    board[move.to] = piece;
  }
  if (kind === "K" && Math.abs(fileOf(move.to) - fileOf(move.from)) === 2) {
    for (const [letter, spec] of Object.entries(CASTLING)) {
      if (
        spec.kingFrom === move.from &&
        spec.kingTo === move.to &&
        (letter === letter.toUpperCase()) === (color === "w")
      ) {
        board[spec.rookTo] = board[spec.rookFrom];
        board[spec.rookFrom] = "";
      }
    }
  }

  let castling = position.castling;
  if (kind === "K")
    castling = [...castling].filter((c) => (c === c.toUpperCase()) !== (color === "w")).join("");
  for (const [letter, spec] of Object.entries(CASTLING)) {
    if (spec.rookFrom === move.from || spec.rookFrom === move.to)
      castling = castling.replace(letter, "");
  }

  let ep: number | null = null;
  if (kind === "P" && Math.abs(move.to - move.from) === 16) ep = move.from + forward;

  return {
    board,
    turn: other(color),
    castling,
    ep,
    halfmove: kind === "P" || captured !== "" ? 0 : position.halfmove + 1,
    fullmove: position.fullmove + (color === "b" ? 1 : 0),
  };
}

export function legalMoves(position: Position): Move[] {
  const color = position.turn;
  return pseudoMoves(position).filter((move) => {
    const after = makeMove(position, move);
    return !isAttacked(after, kingSquare(after, color), other(color));
  });
}

export function isCheckmate(position: Position): boolean {
  return isCheck(position) && legalMoves(position).length === 0;
}

export function isStalemate(position: Position): boolean {
  return !isCheck(position) && legalMoves(position).length === 0;
}

export function perft(position: Position, depth: number): number {
  if (depth === 0) return 1;
  const moves = legalMoves(position);
  if (depth === 1) return moves.length;
  return moves.reduce((total, move) => total + perft(makeMove(position, move), depth - 1), 0);
}

// ---- notation ----------------------------------------------------------------------------

/** Standard algebraic notation of a legal move, with `+` / `#` suffix. */
export function san(position: Position, move: Move, legal?: Move[]): string {
  const { board } = position;
  const piece = board[move.from];
  const kind = piece.toUpperCase();
  const moves = legal ?? legalMoves(position);
  let text: string;
  if (kind === "K" && Math.abs(fileOf(move.to) - fileOf(move.from)) === 2) {
    text = fileOf(move.to) === 6 ? "O-O" : "O-O-O";
  } else {
    const isCapture = board[move.to] !== "" || (kind === "P" && move.to === position.ep);
    if (kind === "P") {
      text = isCapture ? FILES[fileOf(move.from)] : "";
    } else {
      text = kind;
      const rivals = moves.filter(
        (m) => m.to === move.to && m.from !== move.from && board[m.from] === piece,
      );
      if (rivals.length > 0) {
        const sameFile = rivals.some((m) => fileOf(m.from) === fileOf(move.from));
        const sameRank = rivals.some((m) => rankOf(m.from) === rankOf(move.from));
        if (!sameFile) text += FILES[fileOf(move.from)];
        else if (!sameRank) text += String(rankOf(move.from) + 1);
        else text += squareName(move.from);
      }
    }
    if (isCapture) text += "x";
    text += squareName(move.to);
    if (move.promotion) text += "=" + move.promotion.toUpperCase();
  }
  const after = makeMove(position, move);
  if (isCheck(after)) text += legalMoves(after).length === 0 ? "#" : "+";
  return text;
}

/** SAN stripped of everything a human may omit or add: suffixes, `x`, `=`. */
function loose(text: string): string {
  return text
    .trim()
    .replace("0-0-0", "O-O-O")
    .replace("0-0", "O-O")
    .replace(/[+#!?]+$/, "")
    .replace(/[x=:]/g, "");
}

/** Reads a move in UCI (`b3g8`) or SAN (`Qg8+`); null if not a legal move. */
export function parseMove(position: Position, text: string, legal?: Move[]): Move | null {
  const moves = legal ?? legalMoves(position);
  const candidate = text.trim();
  if (UCI_RE.test(candidate.toLowerCase())) {
    const wanted = candidate.toLowerCase();
    const found = moves.find((m) => uci(m) === wanted);
    if (found) return found;
  }
  const wanted = loose(candidate);
  const exact = moves.find((m) => loose(san(position, m, moves)) === wanted);
  if (exact) return exact;
  const lowered = wanted.toLowerCase();
  const found = moves.filter((m) => loose(san(position, m, moves)).toLowerCase() === lowered);
  return found.length === 1 ? found[0] : null;
}
